// src/auth/Auth.tsx
import React, { useState } from "react";
import { useSupabase } from "./useSupabase";
import {
  Box,
  Container,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  LinearProgress,
  Link,
  useTheme,
} from "@mui/material";
import { StandardButton } from "../components/StandardButton";
import { Visibility, VisibilityOff, CheckCircle } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

type AuthMode = "signin" | "signup" | "forgot-password";

interface PasswordStrength {
  score: number;
  label: string;
  color: "error" | "warning" | "info" | "success";
}

const Auth = () => {
  const { signIn, signUp, resetPassword, isConfirmEmail, setIsConfirmEmail } =
    useSupabase();
  const theme = useTheme();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [mode, setMode] = useState<AuthMode>("signin");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");

  const location = useLocation();
  const locationFrom = location.state?.from || "/admin";
  const navigate = useNavigate();

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Password strength calculation
  const calculatePasswordStrength = (pwd: string): PasswordStrength => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z\d]/.test(pwd)) score++;

    if (score <= 2) return { score, label: "Weak", color: "error" };
    if (score === 3) return { score, label: "Fair", color: "warning" };
    if (score === 4) return { score, label: "Good", color: "info" };
    return { score, label: "Strong", color: "success" };
  };

  // Password validation
  const validatePassword = (pwd: string): boolean => {
    if (!pwd) {
      setPasswordError("Password is required");
      return false;
    }
    if (mode === "signup") {
      if (pwd.length < 8) {
        setPasswordError("Password must be at least 8 characters");
        return false;
      }
      if (pwd !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return false;
      }
    }
    setPasswordError("");
    return true;
  };

  // Parse Supabase auth errors
  const parseAuthError = (err: any): string => {
    const message = err?.message || "";

    if (message.includes("Invalid login credentials")) {
      return "Invalid email or password. Please try again.";
    }
    if (message.includes("Email not confirmed")) {
      return "Please confirm your email address before signing in.";
    }
    if (message.includes("User already registered")) {
      return "An account with this email already exists. Please sign in instead.";
    }
    if (message.includes("rate limit")) {
      return "Too many attempts. Please wait a few minutes before trying again.";
    }
    if (message.includes("Password should be at least")) {
      return "Password must be at least 8 characters long.";
    }
    if (message.includes("Unable to validate email")) {
      return "Please enter a valid email address.";
    }
    if (message.includes("network") || message.includes("fetch")) {
      return "Network error. Please check your connection and try again.";
    }

    return message || "An unexpected error occurred. Please try again.";
  };

  const onSignIn = async () => {
    setError("");
    setSuccess("");

    if (!validateEmail(email) || !validatePassword(password)) {
      return;
    }

    setLoading(true);
    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) throw signInError;
      navigate(locationFrom);
    } catch (err: any) {
      setError(parseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSignUp = async () => {
    setError("");
    setSuccess("");

    if (!validateEmail(email) || !validatePassword(password)) {
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password);
      // Success dialog will show via isConfirmEmail state
    } catch (err: any) {
      setError(parseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = async () => {
    setError("");
    setSuccess("");

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await resetPassword(email);
      if (resetError) throw resetError;
      setSuccess(
        "Password reset email sent! Please check your inbox and follow the instructions.",
      );
      // Switch back to sign in after 3 seconds
      setTimeout(() => {
        setMode("signin");
        setSuccess("");
      }, 3000);
    } catch (err: any) {
      setError(parseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (mode === "signin") {
      onSignIn();
    } else if (mode === "signup") {
      onSignUp();
    } else if (mode === "forgot-password") {
      onForgotPassword();
    }
  };

  const handleModeSwitch = (newMode: AuthMode) => {
    setMode(newMode);
    setError("");
    setSuccess("");
    setEmailError("");
    setPasswordError("");
    setPassword("");
    setConfirmPassword("");
  };

  const passwordStrength =
    mode === "signup" ? calculatePasswordStrength(password) : null;

  const getTitle = () => {
    if (mode === "forgot-password") return "Reset Password";
    if (mode === "signup") return "Create Account";
    return "Welcome Back";
  };

  const getSubtitle = () => {
    if (mode === "forgot-password")
      return "Enter your email to receive a password reset link";
    if (mode === "signup") return "Join us today and get started";
    return "Sign in to access your account";
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.background.default,
          py: 4,
          gap: 4,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
            }}
          >
            {loading && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  borderRadius: "8px 8px 0 0",
                }}
              />
            )}

            <Typography
              variant="h4"
              align="center"
              gutterBottom
              fontWeight="bold"
              color="primary"
            >
              {getTitle()}
            </Typography>

            <Typography
              variant="body1"
              align="center"
              sx={{ mb: 3, color: "text.secondary" }}
            >
              {getSubtitle()}
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                onBlur={() => validateEmail(email)}
                error={!!emailError}
                helperText={emailError}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {mode !== "forgot-password" && (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete={
                      mode === "signin" ? "current-password" : "new-password"
                    }
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    error={!!passwordError}
                    helperText={
                      passwordError ||
                      (mode === "signup"
                        ? "At least 8 characters recommended"
                        : "")
                    }
                    disabled={loading}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "hide password" : "show password"
                              }
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 1 }}
                  />

                  {mode === "signup" && password && (
                    <Box sx={{ mb: 2 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Password strength:
                        </Typography>
                        <Typography
                          variant="caption"
                          color={`${passwordStrength?.color}.main`}
                          fontWeight="bold"
                        >
                          {passwordStrength?.label}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(passwordStrength?.score || 0) * 20}
                        color={passwordStrength?.color}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  )}

                  {mode === "signup" && (
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordError("");
                      }}
                      error={!!passwordError && confirmPassword !== ""}
                      helperText={
                        confirmPassword !== "" && password !== confirmPassword
                          ? "Passwords do not match"
                          : ""
                      }
                      disabled={loading}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showConfirmPassword
                                    ? "hide password"
                                    : "show password"
                                }
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                edge="end"
                                disabled={loading}
                              >
                                {showConfirmPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{ mb: 1 }}
                    />
                  )}
                </>
              )}

              {mode === "signin" && (
                <Box sx={{ textAlign: "right", mb: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => handleModeSwitch("forgot-password")}
                    sx={{
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    disabled={loading}
                  >
                    Forgot password?
                  </Link>
                </Box>
              )}

              <StandardButton
                type="submit"
                fullWidth
                loading={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {mode === "forgot-password"
                  ? "Send Reset Link"
                  : mode === "signup"
                    ? "Create Account"
                    : "Sign In"}
              </StandardButton>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Stack direction="row" justifyContent="center" spacing={0.5}>
                {mode === "forgot-password" ? (
                  <>
                    <Typography variant="body1" color="text.secondary">
                      Remember your password?
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="body1"
                      onClick={() => handleModeSwitch("signin")}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      disabled={loading}
                    >
                      Sign In
                    </Link>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" color="text.secondary">
                      {mode === "signin"
                        ? "Don't have an account?"
                        : "Already have an account?"}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="body1"
                      onClick={() =>
                        handleModeSwitch(
                          mode === "signin" ? "signup" : "signin",
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      disabled={loading}
                    >
                      {mode === "signin" ? "Sign Up" : "Sign In"}
                    </Link>
                  </>
                )}
              </Stack>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Confirm Email Dialog */}
      <Dialog
        open={isConfirmEmail}
        maxWidth="sm"
        onClose={() => handleModeSwitch("signin")}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: "primary.main" }}>
          <CheckCircle sx={{ fontSize: 48, mb: 1 }} color="success" />
          <br />
          Email Confirmation Required
        </DialogTitle>
        <DialogContent>
          <DialogContentText align="center">
            A confirmation email has been sent to <strong>{email}</strong>.
            <br />
            <br />
            Please check your inbox and follow the instructions to complete your
            registration.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
          <StandardButton
            size="medium"
            onClick={() => {
              setIsConfirmEmail(false);
              handleModeSwitch("signin");
            }}
          >
            OK
          </StandardButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default Auth;
